import { AxiosError } from "axios"
import { useQueryClient } from "@tanstack/react-query"
import DOMPurify from "dompurify"
import { Form, Formik } from "formik"
import * as yup from "yup"
import cx from "classnames"

import { Toast } from "../../components/Toast"
import { SubmitButton } from "../../components/SubmitButton"
import EligibilityQuizFormInput from "../EligibilityQuiz/EligibilityQuizFormInput"
import { useAccountId, useUpdateGovtAppWizard } from "../../hooks"
import { genericErrMsg } from "../../api/auth"
import { QuizAttestationType } from "@/types/eligibilityQuiz"
import { AccountAttestationType } from "@/types/govtAppWizard"

type FormValues = Record<number, boolean | string | number>

export interface GovtWizardQuizFormProps {
  questionnaireAttestations: QuizAttestationType[] | undefined
  accountAttestations: AccountAttestationType[] | undefined
  quizStep: number
  setQuizStep: (quizStep: number) => void
  setCompleted: (completed: boolean) => void
  imageUrl: string | undefined
}

const validationSchema = (attestations: QuizAttestationType[] = []) => {
  const fields = {}
  const types = {
    bool: yup.bool().oneOf([true], "Required"),
    str: yup
      .string()
      .required("Please fill out this field")
      .max(500, "Maximum length exceeded, please limit to 500 characters"),
    float: yup.number().required("Enter in a number"),
    int: yup.number().integer().required("Enter in a number"),
    multiple_choice: yup.string().required("Choose an option"),
  }

  attestations.forEach((attestation) =>
    Object.assign(fields, {
      [attestation?.id]: types[attestation.response_type],
    })
  )

  return yup.object().shape(fields)
}

const GovtWizardQuizForm = ({
  questionnaireAttestations,
  accountAttestations,
  quizStep,
  setQuizStep,
  setCompleted,
  imageUrl,
}: GovtWizardQuizFormProps) => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()

  const initialValues: FormValues = questionnaireAttestations?.reduce(
    (acc, attestation) => {
      const emptyValues = {
        bool: false,
        str: "",
        float: 0,
        int: 0,
        multiple_choice: "",
      }

      const value =
        accountAttestations?.find(
          (att) => att.attestation_id === attestation?.id.toString()
        )?.value || emptyValues[attestation.response_type]

      return {
        ...acc,
        [attestation?.id]: value,
      }
    },
    {}
  ) as FormValues

  const { mutateAsync: updateGovtAppWizard } = useUpdateGovtAppWizard(
    queryClient,
    accountId,
    {
      onSuccess: () => {
        setCompleted(true)
      },
      onError: (error: AxiosError) => {
        Toast.error(error?.message || genericErrMsg)
      },
    }
  )

  const handleSubmit = async (values: FormValues) => {
    const attData = Object.entries(values)?.map(([attestation_id, value]) => ({
      attestation_id: Number(attestation_id),
      value,
    }))

    await updateGovtAppWizard({
      wizard_key: "govt_application",
      att_data: attData,
    })

    window.scrollTo({
      top: 0,
      behavior: "instant",
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(questionnaireAttestations ?? [])}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => {
        const id = Number(questionnaireAttestations?.[quizStep]?.id)
        let disabled = true

        if (id !== undefined) {
          const value = formikProps.values[id]
          disabled = value === "" || value === false || value == null
        }

        if (!questionnaireAttestations) return null

        return (
          <Form>
            {questionnaireAttestations.map((attestation, index) => (
              <div
                key={attestation?.id}
                className={cx("container max-w-[844px]", {
                  hidden: index !== quizStep,
                })}
              >
                <figure className="flex justify-center mt-7">
                  <img
                    src={imageUrl}
                    alt="quiz step"
                    className="h-[147px] w-auto block"
                  />
                </figure>

                <h2 className="text-charcoal-500 text-xl lg:text-3xl text-center font-display leading-[120%] md:leading-[124%] tracking-[0.4px] md:tracking-[0.6px] mt-7">
                  {questionnaireAttestations[quizStep].text}
                </h2>

                {questionnaireAttestations[quizStep]?.tooltip?.length > 0 ? (
                  <div
                    className="text-dusk-500 text-base text-center font-body leading-[124%] tracking-[0.32px] mt-3 [&_a]:text-leaf [&_a]:font-semibold hover:[&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        questionnaireAttestations[quizStep]?.tooltip,
                        {
                          ADD_ATTR: ["target"],
                        }
                      ),
                    }}
                  />
                ) : null}

                <div className="max-w-[500px] mx-auto md:mt-9">
                  <EligibilityQuizFormInput attestation={attestation} />
                </div>
              </div>
            ))}

            <div className="text-center">
              {questionnaireAttestations &&
              quizStep === questionnaireAttestations.length - 1 ? (
                <SubmitButton
                  className="govt-app-page-2-submit-button btn2 btn2-primary font-semibold my-10 md:my-[62px]"
                  isSubmitting={formikProps.isSubmitting}
                  disabled={disabled}
                >
                  Submit
                </SubmitButton>
              ) : (
                <button
                  type="button"
                  className="govt-app-page-2-next-button btn2 btn2-primary font-semibold my-10 md:my-[62px]"
                  onClick={() => {
                    setQuizStep(quizStep + 1)

                    window.scrollTo({
                      top: 0,
                      behavior: "instant",
                    })
                  }}
                  disabled={disabled}
                >
                  Next
                </button>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default GovtWizardQuizForm
